import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { 
  getComplianceStatus, 
  getAdminComplianceOverview,
  getActiveEmergencySessions,
  getPendingReviews,
  revokeEmergencyAccess,
  submitReviewDecision,
  requestEmergencyAccess,
  getAssignments,
  getMyPatients,
  getMyCareTeam
} from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import TwoFactorSetup from "@/components/TwoFactorSetup";
import { 
  Shield, ShieldCheck, Clock, Users, AlertTriangle, FileText, Lock, 
  CheckCircle, XCircle, Loader2, RefreshCw, UserCheck, Activity,
  Eye, Ban, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

export default function ComplianceDashboard() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [adminOverview, setAdminOverview] = useState(null);
  const [emergencySessions, setEmergencySessions] = useState([]);
  const [pendingReviews, setPendingReviews] = useState({ reviews: [], counts: {} });
  const [assignments, setAssignments] = useState([]);
  const [careTeam, setCareTeam] = useState([]);
  const [myPatients, setMyPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [emergencyOpen, setEmergencyOpen] = useState(false);
  const [emergencyForm, setEmergencyForm] = useState({ reason: "", urgency: "medium", patientId: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "administrator";
  const isStaff = user?.role === "staff" || user?.role === "provider" || isAdmin;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const statusData = await getComplianceStatus();
      setStatus(statusData);
      
      if (isAdmin) {
        const [overview, sessions, reviews, allAssignments] = await Promise.all([
          getAdminComplianceOverview(),
          getActiveEmergencySessions(),
          getPendingReviews(),
          getAssignments()
        ]);
        setAdminOverview(overview);
        setEmergencySessions(sessions.sessions || []);
        setPendingReviews(reviews);
        setAssignments(allAssignments.assignments || []);
      }
      
      if (isStaff && !isAdmin) {
        const patients = await getMyPatients();
        setMyPatients(patients.patients || []);
      }
      
      if (!isStaff) {
        const team = await getMyCareTeam();
        setCareTeam(team.care_team || []);
      }
    } catch (error) {
      console.error("Failed to load compliance data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeAccess = async (sessionId) => {
    try {
      await revokeEmergencyAccess(sessionId);
      toast.success("Emergency access revoked");
      loadData();
    } catch (error) {
      toast.error("Failed to revoke access");
    }
  };

  const handleReviewDecision = async (reviewId, decision) => {
    try {
      await submitReviewDecision(reviewId, decision);
      toast.success(`Review ${decision}`);
      loadData();
    } catch (error) {
      toast.error("Failed to submit decision");
    }
  };

  const handleEmergencyRequest = async () => {
    if (!emergencyForm.reason) {
      toast.error("Please provide a reason");
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await requestEmergencyAccess(
        emergencyForm.reason,
        emergencyForm.urgency,
        emergencyForm.patientId || null
      );
      toast.success(response.message);
      setEmergencyOpen(false);
      setEmergencyForm({ reason: "", urgency: "medium", patientId: "" });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request access");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getIconForFeature = (iconName) => {
    const icons = {
      clock: Clock,
      shield: Shield,
      "file-text": FileText,
      lock: Lock,
      users: Users,
      "alert-triangle": AlertTriangle
    };
    const Icon = icons[iconName] || Shield;
    return <Icon className="w-5 h-5" />;
  };

  if (isLoading) {
    return (
      <div className="container-max px-6 py-12 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container-max px-6 py-8" data-testid="compliance-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">HIPAA Compliance</h1>
          <p className="text-muted-foreground">Security and compliance settings</p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={isLoading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="status" className="space-y-6">
        <TabsList>
          <TabsTrigger value="status">My Status</TabsTrigger>
          {isStaff && <TabsTrigger value="emergency">Emergency Access</TabsTrigger>}
          {isAdmin && <TabsTrigger value="reviews">Pending Reviews</TabsTrigger>}
          {isAdmin && <TabsTrigger value="overview">Admin Overview</TabsTrigger>}
          <TabsTrigger value="team">{isStaff ? "My Patients" : "Care Team"}</TabsTrigger>
        </TabsList>

        {/* My Status Tab */}
        <TabsContent value="status" className="space-y-6">
          {/* Session & 2FA Status Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Session Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-primary" />
                  Session Status
                </CardTitle>
                <CardDescription>Your current session information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Time Remaining</span>
                  <Badge variant={status?.session?.remaining_minutes <= 2 ? "destructive" : "default"}>
                    {Math.floor(status?.session?.remaining_minutes || 0)} min
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Session Duration</span>
                  <span className="text-sm font-medium">{status?.session?.session_duration_minutes} minutes</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ 
                      width: `${Math.min(100, (status?.session?.remaining_seconds / (status?.session?.session_duration_minutes * 60)) * 100)}%` 
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* 2FA Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5 text-primary" />
                  Two-Factor Authentication
                </CardTitle>
                <CardDescription>Extra security for your account</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <Badge variant={status?.two_factor?.is_enabled ? "default" : "secondary"}>
                    {status?.two_factor?.is_enabled ? (
                      <>
                        <ShieldCheck className="w-3 h-3 mr-1" />
                        Enabled
                      </>
                    ) : (
                      "Disabled"
                    )}
                  </Badge>
                </div>
                {status?.two_factor?.is_enabled && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">This Session</span>
                    <Badge variant={status?.two_factor?.is_verified_this_session ? "default" : "outline"}>
                      {status?.two_factor?.is_verified_this_session ? "Verified" : "Not Verified"}
                    </Badge>
                  </div>
                )}
                <TwoFactorSetup 
                  trigger={
                    <Button variant="outline" className="w-full" data-testid="2fa-settings-btn">
                      <Shield className="w-4 h-4 mr-2" />
                      {status?.two_factor?.is_enabled ? "Manage 2FA" : "Enable 2FA"}
                    </Button>
                  }
                />
              </CardContent>
            </Card>
          </div>

          {/* Compliance Features */}
          <Card>
            <CardHeader>
              <CardTitle>Active Security Features</CardTitle>
              <CardDescription>HIPAA compliance protections on your account</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {status?.compliance_features?.map((feature, i) => (
                  <div 
                    key={i}
                    className="flex items-start gap-3 p-4 rounded-lg border bg-card"
                  >
                    <div className={`p-2 rounded-lg ${
                      feature.status === 'active' || feature.status === 'enabled' 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {getIconForFeature(feature.icon)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{feature.name}</p>
                        <Badge variant={feature.status === 'active' || feature.status === 'enabled' ? 'default' : 'secondary'} className="text-xs">
                          {feature.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Emergency Access Status */}
          {status?.emergency_access?.is_active && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-5 h-5" />
                  Emergency Access Active
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-amber-700">
                  You have active emergency access until {new Date(status.emergency_access.expires_at).toLocaleString()}.
                  All actions are being logged.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Emergency Access Tab */}
        {isStaff && (
          <TabsContent value="emergency" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Emergency Access</CardTitle>
                    <CardDescription>Break-glass access for urgent situations</CardDescription>
                  </div>
                  <Dialog open={emergencyOpen} onOpenChange={setEmergencyOpen}>
                    <DialogTrigger asChild>
                      <Button variant="destructive" data-testid="request-emergency-btn">
                        <AlertTriangle className="w-4 h-4 mr-2" />
                        Request Emergency Access
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Request Emergency Access</DialogTitle>
                        <DialogDescription>
                          This will grant temporary elevated access. All actions will be logged.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <Label>Reason for Access</Label>
                          <Textarea
                            placeholder="Describe why emergency access is needed..."
                            value={emergencyForm.reason}
                            onChange={(e) => setEmergencyForm(prev => ({ ...prev, reason: e.target.value }))}
                            data-testid="emergency-reason-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Urgency Level</Label>
                          <Select
                            value={emergencyForm.urgency}
                            onValueChange={(v) => setEmergencyForm(prev => ({ ...prev, urgency: v }))}
                          >
                            <SelectTrigger data-testid="emergency-urgency-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button 
                          className="w-full"
                          onClick={handleEmergencyRequest}
                          disabled={isSubmitting}
                          data-testid="submit-emergency-btn"
                        >
                          {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                          Request Access
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {isAdmin && emergencySessions.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Urgency</TableHead>
                        <TableHead>Granted</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {emergencySessions.map((session) => (
                        <TableRow key={session.id}>
                          <TableCell className="font-medium">{session.user_name}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{session.reason}</TableCell>
                          <TableCell>
                            <Badge variant={session.urgency === 'critical' ? 'destructive' : 'outline'}>
                              {session.urgency}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(session.granted_at).toLocaleString()}</TableCell>
                          <TableCell>{new Date(session.expires_at).toLocaleString()}</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleRevokeAccess(session.id)}
                              data-testid={`revoke-access-${session.id}`}
                            >
                              <Ban className="w-4 h-4 mr-1" />
                              Revoke
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No active emergency access sessions</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Pending Reviews Tab (Admin) */}
        {isAdmin && (
          <TabsContent value="reviews" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Pending AI Reviews</CardTitle>
                    <CardDescription>Decisions requiring human approval</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="destructive">{pendingReviews.counts?.high || 0} High</Badge>
                    <Badge variant="outline">{pendingReviews.counts?.medium || 0} Medium</Badge>
                    <Badge variant="secondary">{pendingReviews.counts?.low || 0} Low</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {pendingReviews.reviews?.length > 0 ? (
                  <div className="space-y-4">
                    {pendingReviews.reviews.map((review) => (
                      <div key={review.id} className="p-4 border rounded-lg space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">{review.title}</h4>
                            <p className="text-sm text-muted-foreground">{review.description}</p>
                          </div>
                          <Badge variant={review.priority === 'high' ? 'destructive' : 'outline'}>
                            {review.priority}
                          </Badge>
                        </div>
                        <div className="p-3 bg-muted rounded-lg">
                          <p className="text-sm font-medium mb-1">AI Suggestion:</p>
                          <p className="text-sm">{review.ai_suggestion}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm"
                            onClick={() => handleReviewDecision(review.id, 'approved')}
                            data-testid={`approve-review-${review.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleReviewDecision(review.id, 'rejected')}
                            data-testid={`reject-review-${review.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pending reviews</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Admin Overview Tab */}
        {isAdmin && adminOverview && (
          <TabsContent value="overview" className="space-y-6">
            {/* Compliance Score */}
            <Card>
              <CardHeader>
                <CardTitle>Compliance Score</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className={`text-5xl font-bold ${
                    adminOverview.compliance_score?.score >= 90 ? 'text-green-600' :
                    adminOverview.compliance_score?.score >= 75 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {adminOverview.compliance_score?.score}
                  </div>
                  <div>
                    <p className="font-medium">{adminOverview.compliance_score?.rating}</p>
                    {adminOverview.compliance_score?.issues?.length > 0 && (
                      <ul className="text-sm text-muted-foreground mt-1">
                        {adminOverview.compliance_score.issues.map((issue, i) => (
                          <li key={i}>• {issue}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{adminOverview.users?.total}</p>
                      <p className="text-sm text-muted-foreground">Total Users</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-green-100">
                      <ShieldCheck className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{adminOverview.users?.['2fa_adoption_rate']}%</p>
                      <p className="text-sm text-muted-foreground">2FA Adoption</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-amber-100">
                      <Activity className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{adminOverview.sessions?.active}</p>
                      <p className="text-sm text-muted-foreground">Active Sessions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-red-100">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{adminOverview.audit?.security_events_last_24h}</p>
                      <p className="text-sm text-muted-foreground">Security Events (24h)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Patient Assignments */}
            <Card>
              <CardHeader>
                <CardTitle>Patient-Staff Assignments</CardTitle>
                <CardDescription>{assignments.length} active assignments</CardDescription>
              </CardHeader>
              <CardContent>
                {assignments.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient</TableHead>
                        <TableHead>Staff</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Assigned</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignments.slice(0, 10).map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>{a.patient_name}</TableCell>
                          <TableCell>{a.staff_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{a.assignment_type}</Badge>
                          </TableCell>
                          <TableCell>{new Date(a.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center py-4 text-muted-foreground">No assignments yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Care Team / My Patients Tab */}
        <TabsContent value="team" className="space-y-6">
          {isStaff ? (
            <Card>
              <CardHeader>
                <CardTitle>My Assigned Patients</CardTitle>
                <CardDescription>Patients you are responsible for</CardDescription>
              </CardHeader>
              <CardContent>
                {myPatients.length > 0 ? (
                  <div className="space-y-4">
                    {myPatients.map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <UserCheck className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{p.patient_name}</p>
                            <p className="text-sm text-muted-foreground">{p.patient_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge variant="outline">{p.assignment_type}</Badge>
                          {p.last_journal_date && (
                            <span className="text-xs text-muted-foreground">
                              Last entry: {p.last_journal_date}
                            </span>
                          )}
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No patients assigned yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>My Care Team</CardTitle>
                <CardDescription>Staff members assigned to your care</CardDescription>
              </CardHeader>
              <CardContent>
                {careTeam.length > 0 ? (
                  <div className="space-y-4">
                    {careTeam.map((member) => (
                      <div key={member.assignment_id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <UserCheck className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{member.staff_name}</p>
                            <p className="text-sm text-muted-foreground">{member.role}</p>
                          </div>
                        </div>
                        <Badge variant={member.assignment_type === 'primary' ? 'default' : 'outline'}>
                          {member.assignment_type}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No care team assigned yet</p>
                    <p className="text-sm">Your hospice team will be assigned soon</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
